/**
 * FlowEngine — Deterministic Flow Processor
 *
 * CORE RULE: AI handles conversation. Code handles decisions.
 *
 * This engine evaluates flow nodes, checks rules against collected
 * answers, and determines the next step. It has ZERO dependencies
 * on React, Supabase, AI, or any external system.
 *
 * It is a pure function machine:
 *   (flow + session + user_answer) → next_node
 */

import {
  FlowDefinition,
  FlowNode,
  FlowRule,
  FlowSession,
  FlowStepResult,
  FlowEvaluationResult,
  RuleOperator,
  RouteDestination,
  NodeValidation,
} from './types';

// ─── Engine ────────────────────────────────────────────────

export class FlowEngine {
  private flow: FlowDefinition;

  constructor(flow: FlowDefinition) {
    this.flow = flow;
  }

  /**
   * Start a new session for this flow.
   * Returns the first node to present.
   */
  startSession(sessionId: string): FlowStepResult {
    const startNode = this.flow.nodes[this.flow.start_node];
    if (!startNode) {
      throw new FlowError(`Start node "${this.flow.start_node}" not found in flow "${this.flow.id}"`);
    }

    const session: FlowSession = {
      id: sessionId,
      flow_id: this.flow.id,
      current_node_id: this.flow.start_node,
      answers: {},
      history: [this.flow.start_node],
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    return this.buildStepResult(startNode, session);
  }

  /**
   * Process a user's answer and advance the flow.
   *
   * 1. Store the answer under the current node's field name
   * 2. Evaluate rules on the current node
   * 3. Determine the next node
   * 4. If next is a decision/route node, auto-advance through it
   * 5. Return the next node to present to the user
   */
  processAnswer(session: FlowSession, answer: unknown): FlowStepResult {
    if (session.status !== 'active') {
      throw new FlowError(`Session "${session.id}" is not active (status: ${session.status})`);
    }

    const currentNode = this.getNode(session.current_node_id);

    // Step 1: Store the answer
    if (currentNode.field) {
      session.answers[currentNode.field] = answer;
    }
    session.updated_at = new Date().toISOString();

    // Step 2: Evaluate rules to find next node
    const evaluation = this.evaluateNode(currentNode, session.answers);

    if (!evaluation.next_node_id) {
      // No next node — flow ends here
      session.status = 'completed';
      return {
        node: currentNode,
        ended: true,
        session,
      };
    }

    // Step 3: Move to next node
    const nextNode = this.getNode(evaluation.next_node_id);
    session.current_node_id = evaluation.next_node_id;
    session.history.push(evaluation.next_node_id);

    // Step 4: Auto-advance through non-interactive nodes (decision, route)
    return this.advanceThroughNonInteractive(nextNode, session);
  }

  /**
   * Evaluate a node's rules against current answers.
   * Returns which node to go to next.
   */
  evaluateNode(node: FlowNode, answers: Record<string, unknown>): FlowEvaluationResult {
    // Check rules in order — first match wins
    for (const rule of node.rules) {
      if (this.evaluateRule(rule, answers)) {
        const targetNode = this.flow.nodes[rule.next_node];
        return {
          next_node_id: rule.next_node,
          matched_rule: rule,
          is_terminal: targetNode?.type === 'end',
        };
      }
    }

    // No rule matched — use default next
    const defaultNext = node.next[0] ?? null;
    if (defaultNext) {
      const targetNode = this.flow.nodes[defaultNext];
      return {
        next_node_id: defaultNext,
        matched_rule: null,
        is_terminal: targetNode?.type === 'end',
      };
    }

    // No rules, no default — terminal
    return {
      next_node_id: null,
      matched_rule: null,
      is_terminal: true,
    };
  }

  /**
   * Evaluate a single rule against collected answers.
   */
  evaluateRule(rule: FlowRule, answers: Record<string, unknown>): boolean {
    const value = answers[rule.field];
    return FlowEngine.compare(value, rule.operator, rule.value);
  }

  /**
   * Validate a user's answer against a node's validation rules.
   * Returns { valid: true } or { valid: false, error: string }.
   */
  validateAnswer(node: FlowNode, answer: unknown): { valid: boolean; error?: string } {
    if (!node.validation) return { valid: true };
    const v = node.validation;

    if (v.required && (answer === undefined || answer === null || answer === '')) {
      return { valid: false, error: v.errorMessage || 'This field is required' };
    }

    if (answer === undefined || answer === null || answer === '') {
      return { valid: true }; // Not required and empty — OK
    }

    const strValue = String(answer);

    if (v.pattern) {
      const regex = new RegExp(v.pattern);
      if (!regex.test(strValue)) {
        return { valid: false, error: v.errorMessage || 'Invalid format' };
      }
    }

    if (v.minLength !== undefined && strValue.length < v.minLength) {
      return { valid: false, error: v.errorMessage || `Must be at least ${v.minLength} characters` };
    }

    if (v.maxLength !== undefined && strValue.length > v.maxLength) {
      return { valid: false, error: v.errorMessage || `Must be at most ${v.maxLength} characters` };
    }

    const numValue = Number(answer);
    if (!isNaN(numValue)) {
      if (v.min !== undefined && numValue < v.min) {
        return { valid: false, error: v.errorMessage || `Must be at least ${v.min}` };
      }
      if (v.max !== undefined && numValue > v.max) {
        return { valid: false, error: v.errorMessage || `Must be at most ${v.max}` };
      }
    }

    return { valid: true };
  }

  /**
   * Get a node by ID, or throw.
   */
  getNode(nodeId: string): FlowNode {
    const node = this.flow.nodes[nodeId];
    if (!node) {
      throw new FlowError(`Node "${nodeId}" not found in flow "${this.flow.id}"`);
    }
    return node;
  }

  /**
   * Get the full flow definition.
   */
  getFlow(): FlowDefinition {
    return this.flow;
  }

  /**
   * Validate the entire flow definition for structural issues.
   * Returns an array of error messages (empty = valid).
   */
  validate(): string[] {
    const errors: string[] = [];

    // Check start node exists
    if (!this.flow.nodes[this.flow.start_node]) {
      errors.push(`Start node "${this.flow.start_node}" does not exist`);
    }

    // Check all rule targets exist
    for (const [nodeId, node] of Object.entries(this.flow.nodes)) {
      for (const rule of node.rules) {
        if (!this.flow.nodes[rule.next_node]) {
          errors.push(`Node "${nodeId}" has rule pointing to non-existent node "${rule.next_node}"`);
        }
      }
      for (const nextId of node.next) {
        if (!this.flow.nodes[nextId]) {
          errors.push(`Node "${nodeId}" has default next pointing to non-existent node "${nextId}"`);
        }
      }
    }

    // Check for unreachable nodes
    const reachable = new Set<string>();
    const queue = [this.flow.start_node];
    while (queue.length > 0) {
      const id = queue.pop()!;
      if (reachable.has(id)) continue;
      reachable.add(id);
      const node = this.flow.nodes[id];
      if (!node) continue;
      for (const rule of node.rules) {
        queue.push(rule.next_node);
      }
      for (const nextId of node.next) {
        queue.push(nextId);
      }
    }

    for (const nodeId of Object.keys(this.flow.nodes)) {
      if (!reachable.has(nodeId)) {
        errors.push(`Node "${nodeId}" is unreachable from start`);
      }
    }

    return errors;
  }

  // ─── Private ───────────────────────────────────────────

  /**
   * Auto-advance through decision and route nodes until
   * we hit an interactive node (question, collect) or end.
   */
  private advanceThroughNonInteractive(node: FlowNode, session: FlowSession): FlowStepResult {
    let current = node;
    let safetyCounter = 0;
    const MAX_CHAIN = 50; // Prevent infinite loops

    while (safetyCounter < MAX_CHAIN) {
      safetyCounter++;

      if (current.type === 'end') {
        session.status = 'completed';
        return { node: current, ended: true, session };
      }

      if (current.type === 'route') {
        session.status = 'routed';
        const destination = this.extractRouteDestination(current);
        session.route_destination = destination;
        return { node: current, ended: true, destination, session };
      }

      // decision nodes auto-evaluate without user input
      if (current.type === 'decision') {
        const evaluation = this.evaluateNode(current, session.answers);
        if (!evaluation.next_node_id) {
          session.status = 'completed';
          return { node: current, ended: true, session };
        }
        current = this.getNode(evaluation.next_node_id);
        session.current_node_id = evaluation.next_node_id;
        session.history.push(evaluation.next_node_id);
        continue;
      }

      // Interactive node (question, collect) — return to user
      return this.buildStepResult(current, session);
    }

    throw new FlowError(`Decision chain exceeded ${MAX_CHAIN} nodes — possible infinite loop in flow "${this.flow.id}"`);
  }

  private extractRouteDestination(node: FlowNode): RouteDestination {
    return {
      type: (node.meta?.route_type as RouteDestination['type']) || 'webhook',
      url: node.meta?.url as string | undefined,
      payload: node.meta?.payload as Record<string, unknown> | undefined,
      meta: node.meta,
    };
  }

  private buildStepResult(node: FlowNode, session: FlowSession): FlowStepResult {
    return {
      node,
      ended: false,
      session,
    };
  }

  // ─── Static Comparator ─────────────────────────────────

  /**
   * Pure comparison function. No side effects.
   * Extracted as static so it can be reused by other systems
   * (e.g., migrating TriggerExecutor conditions).
   */
  static compare(actual: unknown, operator: RuleOperator, expected?: unknown): boolean {
    switch (operator) {
      case 'exists':
        return actual !== undefined && actual !== null && actual !== '';

      case 'not_exists':
        return actual === undefined || actual === null || actual === '';

      case 'eq':
        return String(actual ?? '').toLowerCase() === String(expected ?? '').toLowerCase();

      case 'neq':
        return String(actual ?? '').toLowerCase() !== String(expected ?? '').toLowerCase();

      case 'gt': {
        const a = Number(actual);
        const b = Number(expected);
        return !isNaN(a) && !isNaN(b) && a > b;
      }

      case 'lt': {
        const a = Number(actual);
        const b = Number(expected);
        return !isNaN(a) && !isNaN(b) && a < b;
      }

      case 'gte': {
        const a = Number(actual);
        const b = Number(expected);
        return !isNaN(a) && !isNaN(b) && a >= b;
      }

      case 'lte': {
        const a = Number(actual);
        const b = Number(expected);
        return !isNaN(a) && !isNaN(b) && a <= b;
      }

      case 'contains':
        return String(actual ?? '').toLowerCase().includes(String(expected ?? '').toLowerCase());

      case 'not_contains':
        return !String(actual ?? '').toLowerCase().includes(String(expected ?? '').toLowerCase());

      case 'between': {
        if (!Array.isArray(expected) || expected.length !== 2) return false;
        const val = Number(actual);
        const [min, max] = expected.map(Number);
        return !isNaN(val) && !isNaN(min) && !isNaN(max) && val >= min && val <= max;
      }

      case 'in': {
        if (!Array.isArray(expected)) return false;
        const normalizedActual = String(actual ?? '').toLowerCase();
        return expected.some(v => String(v).toLowerCase() === normalizedActual);
      }

      default:
        return false;
    }
  }
}

// ─── Error Class ─────────────────────────────────────────

export class FlowError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FlowError';
  }
}
